/**
 * Device thumbnails for the "Devices & service fees" catalog (openspec long-form-ai).
 *
 * Kept SEPARATE from `vhdpPlanData` on purpose: that file is coverage facts sourced from the VHDP
 * printable sheet only, while these images are presentational and come from a different source —
 * Asurion's Verizon Home Device Protect "what's covered" page, mapped to their Contentful assets by
 * Ricardo (2026-08-27). Space `16nm6vz43ids`; the CDN URL is the current published asset.
 *
 * V0 hardcodes the stable `images.ctfassets.net` URLs, the same way `vhdpPlanData` is static client
 * data until Contentful feeds the plan-details surface directly. Keyed by the device `name` exactly
 * as it appears in `vhdpPlanData` so `DevicesTab` can look it up. A device with no entry (e.g.
 * "Original remotes", which has no image on the source page) simply renders without a thumbnail.
 *
 * A few devices legitimately share one asset (routers/modems, smoke/CO detectors, the two DVD lines),
 * mirroring the source page.
 */
export const deviceImageByName: Readonly<Record<string, string>> = {
    // Home entertainment
    TVs: "https://images.ctfassets.net/16nm6vz43ids/5432dvvDubafx9UlniRxVt/9d26ec8fca0cce3c6a77e85dc2384e41/devices-television.png",
    "Gaming systems & handheld gaming devices":
        "https://images.ctfassets.net/16nm6vz43ids/6zW8pVmO33n4tavBOtKUgT/6a58e30f7f84bc013ea017714d76554b/devices-gaming.png",
    "Virtual reality headsets":
        "https://images.ctfassets.net/16nm6vz43ids/1worKFcH449ExbzgTc8cOB/2ec16e670fa0aef233e7ac55d2029fcf/vr-headsets.png",
    "Portable DVD/Blu-ray players":
        "https://images.ctfassets.net/16nm6vz43ids/GPtScucVCNhjHeLtEHlnC/16fd380afc12e5022051d18475d5304b/devices-dvd_bluray.png",
    "Gaming controllers":
        "https://images.ctfassets.net/16nm6vz43ids/5uKm3O7bhE2OEzYxlBPyNG/4739f6a25fee705db6454cdb7ff0f5db/gaming-controllers.png",
    "Audio headsets":
        "https://images.ctfassets.net/16nm6vz43ids/2SkmwxfC34pZIf0HnVzD2z/7b0336db3310d67d8c599c9c5475d1e4/devices-headphones.png",
    "Home theater systems":
        "https://images.ctfassets.net/16nm6vz43ids/1o7n959BncE4rxokEwrD9W/ee2c43992ab0cb802d57f5a574d8896e/subwoofers-image.png",
    "Bluetooth & Wi-Fi enabled speakers":
        "https://images.ctfassets.net/16nm6vz43ids/2oa0YueWDtMDhZxXqTtqfd/a31a6679729b67a8f0406b3e22a54a48/devices-speakers.png",
    "DVD & Blu-ray players":
        "https://images.ctfassets.net/16nm6vz43ids/GPtScucVCNhjHeLtEHlnC/16fd380afc12e5022051d18475d5304b/devices-dvd_bluray.png",
    "Audio/video streaming devices":
        "https://images.ctfassets.net/16nm6vz43ids/1p5ASzU5XbG7dkJR17AfvK/593cb277f1cc781465fac6e045b66965/devices-streaming.png",
    // "Original remotes" — no image on the source page.

    // Smart home
    "Smart door locks":
        "https://images.ctfassets.net/16nm6vz43ids/6nJnBxvkUZ9Xwpvwp2BuXm/431b686f5f04369f1c99f41b82ab82a5/device-lock.png",
    "Smart thermostats":
        "https://images.ctfassets.net/16nm6vz43ids/3lWPu8FUFEs6snUFk5TIhF/22bb57a0c9c61d2240f0a83c11bbee19/smart-thermostat.png",
    "Smart home security cameras":
        "https://images.ctfassets.net/16nm6vz43ids/Ok0HGSMkxd8JThSUWW5lK/c998790a8ec86fe04e369de3e472f2c9/device-securitycamera.png",
    "Smart video doorbells":
        "https://images.ctfassets.net/16nm6vz43ids/27m130cSFQPR3RKeM5GbAz/ad3cebd6d4fc813ec5238d94682314a5/smart-video-doorbell.png",
    "Smart video products":
        "https://images.ctfassets.net/16nm6vz43ids/27m130cSFQPR3RKeM5GbAz/ad3cebd6d4fc813ec5238d94682314a5/smart-video-doorbell.png",
    "Smart device hubs":
        "https://images.ctfassets.net/16nm6vz43ids/zpAtRYtZrALgBCSp0CAvO/e771ea484441b60e6b97bcb2246e07da/Smart-device-hubs.png",
    "Smart light dimmers":
        "https://images.ctfassets.net/16nm6vz43ids/5A7Rf5Qm9STDuPVRoAhAzo/1e421bfb05529b16bb7c65a1d8d017c6/smart-light-dimmers.png",
    "Smart smoke detectors":
        "https://images.ctfassets.net/16nm6vz43ids/gKDDysNhtfvUPViOcXMZE/5e036fd023dbd17bbb3234b0aed1f1eb/device-smokealarm.png",
    "Smart carbon monoxide detectors":
        "https://images.ctfassets.net/16nm6vz43ids/gKDDysNhtfvUPViOcXMZE/5e036fd023dbd17bbb3234b0aed1f1eb/device-smokealarm.png",
    "Smart alarm glassbreak sensors":
        "https://images.ctfassets.net/16nm6vz43ids/53WTIsXkDovydMMLc813q9/b50e633ef788fb93eaff925d22abb739/device-smartalarmglassbreak.png",
    "Smart alarm flood/freeze sensors":
        "https://images.ctfassets.net/16nm6vz43ids/4JoH1XXfIoM442JVdWFx9V/acec546aa9dda7c0b8d8d9e58ad101f5/device-smartalarmfloodfreezesensors.png",
    "Smart alarm keypads":
        "https://images.ctfassets.net/16nm6vz43ids/4LgULxbQKMOtHVl7Pgkarr/da36fed23e1ba81fc3cc4d3a7fefb03f/device-smartalarmkeypads.png",
    "Smart alarm motion detectors":
        "https://images.ctfassets.net/16nm6vz43ids/7qmz10eNLkOrv05HcCj2e0/9ad749108e9273939259f0ff1e9122a4/device-smartalarmmotion.png",
    "Smart alarm range extenders":
        "https://images.ctfassets.net/16nm6vz43ids/3HG92RftsrAx4hIiApqMui/49b80fa42dc0ed86ba9d129499cccff7/device-smartalarmextender.png",
    "Smart alarm contact sensors":
        "https://images.ctfassets.net/16nm6vz43ids/7zPcLSHrGJYUx4zWhDgYgC/cd54aaf1e5a1af0829f1dcddc2dc90bc/device-smartalarmcontactsensor.png",
    "Smart alarm panic buttons":
        "https://images.ctfassets.net/16nm6vz43ids/3VyPTvDbUUwhtT0xGByEp3/f8ea54a49168756a71afe06c15de9d25/device-smartalarmpanic.png",

    // Smart home healthcare
    "Pulse oximeters":
        "https://images.ctfassets.net/16nm6vz43ids/2ZKHpAv0k0QZWPTu66e46s/9d3ad5e52f821609ba8742fd0c8f2427/smart-pulse-oximeters.png",
    EKGs: "https://images.ctfassets.net/16nm6vz43ids/uHdwqTDadpBImNt2g5hqB/4f74cb865f158eb9a70a3ae850c9e9b5/smart-personal-home-use-ekg-monitors.png",
    "Blood pressure monitors":
        "https://images.ctfassets.net/16nm6vz43ids/iMh16n44aFH929PdlpNrO/46cf169ceae45f109982aad774b61fd5/smart-blood-pressure-monitors.png",

    // Smart pet tech
    "Pet collars":
        "https://images.ctfassets.net/16nm6vz43ids/1wvseNyTMAP7RTWSddlQbc/5b1ef98a59c1dcbf02100b305f5aad81/smart-collars.png",
    "Auto fetch machines":
        "https://images.ctfassets.net/16nm6vz43ids/3kF4OUmj1Hx9aQDngZBrhv/cf4a553751a519b739caa2deac53e657/smart-auto-fetch-machines.png",
    "Pet snack and food dispensers":
        "https://images.ctfassets.net/16nm6vz43ids/3eCYAcut1d1HaZdVXE4fW1/6fc10087375acab4eb4bec5fe385c807/smart-pet-food-dispensers.png",

    // Smart floorcare
    "Robotic mops":
        "https://images.ctfassets.net/16nm6vz43ids/6DO7jnbeEDfKddRhmzpVbK/a49e9e6bf5506203d30dc48ed3a87e4d/robotic-mops.png",
    "Robotic vacuums":
        "https://images.ctfassets.net/16nm6vz43ids/Bf8rd7lsRnAHTCeotvcFv/468a42123db1c7580295d59c105653a9/smart-vacuum.png",

    // Wearables
    "Augmented reality glasses":
        "https://images.ctfassets.net/16nm6vz43ids/2aeDXMlL9DlTtSbaPjdZEe/c1648374be0c18af43e0858b19536dff/augmented-reality-glasses.png",
    Smartwatches:
        "https://images.ctfassets.net/16nm6vz43ids/0ILxuFJdTDYp4fFGzYkTe/3548e1943f5ea539e903921091696beb/smart-watches.png",
    "Health and fitness bands":
        "https://images.ctfassets.net/16nm6vz43ids/5DORSt83pIbFOtxyU7C0T3/65cae934c644fe906a5ebbcccd694956/health-and-fitness-bands.png",

    // Home office
    "Desktop computers":
        "https://images.ctfassets.net/16nm6vz43ids/6cpn36S1CidCk6LOcoIbv0/31ad36e1712d93dbfd1e17265fb7f9b9/devices-computer.png",
    Laptops:
        "https://images.ctfassets.net/16nm6vz43ids/1nbuDnIywF9MBXMA8cny4l/be17a1426a83fe9b8daa2ddf8e029225/devices-laptops.png",
    Tablets:
        "https://images.ctfassets.net/16nm6vz43ids/7fr467xcuo9oW93223Tv8Q/adae1d11b4dffed9a4da7757e9de1cbb/devices-tablets.png",
    Printers:
        "https://images.ctfassets.net/16nm6vz43ids/6D5qRb5fvKlo3VTO7EIcWA/5cbcca14aa5e808e59cb9f542f65c3d9/devices-printer.png",
    "Mesh home routers":
        "https://images.ctfassets.net/16nm6vz43ids/79L4hqUio0urAePndoouXZ/3a7bd61496c366569d55cc66f038d194/devices-routers.png",
    "Non-mesh home routers":
        "https://images.ctfassets.net/16nm6vz43ids/79L4hqUio0urAePndoouXZ/3a7bd61496c366569d55cc66f038d194/devices-routers.png",
    "External hard drives":
        "https://images.ctfassets.net/16nm6vz43ids/21VqHSVs1nZv3HADgudddF/df3da78e2c398d39e8736afe903d5324/devices-harddrives.png",
    "Keyboard & mouse":
        "https://images.ctfassets.net/16nm6vz43ids/1ka88tOEx9QMOFsbU0pKlo/eba48722948a9e84cea1f51f88f761d0/devices-keyboard.png",
    "External monitors":
        "https://images.ctfassets.net/16nm6vz43ids/5SKUSeMb1kMDcRzTY1gQc3/37bc40275f3d1677f2a0cc1ee063cb72/devices-monitors.png",
    Modems: "https://images.ctfassets.net/16nm6vz43ids/79L4hqUio0urAePndoouXZ/3a7bd61496c366569d55cc66f038d194/devices-routers.png",
    "External desktop speaker sets":
        "https://images.ctfassets.net/16nm6vz43ids/2oa0YueWDtMDhZxXqTtqfd/a31a6679729b67a8f0406b3e22a54a48/devices-speakers.png",
};

/** The thumbnail URL for a device, or undefined if the source page has no image for it. */
export const deviceImageUrl = (deviceName: string): string | undefined => deviceImageByName[deviceName];
